import React, { useState, useEffect } from "react";
import { useDiscount } from "../hooks/useDiscount";
import CommonButtons from "../../../../../../shared/components/CommonButtons";
import SelectCustomer from "../SelectCustomer";
import ConfirmActionModal from "./ConfirmActionModal";
import { PERMISSIONS, useCheckPermission } from "src/permissionCheck/PermossionChick";
import { toBoolean, useUser } from "src/shared/context/UserContext";

interface BillSummaryProps {
  subtotal: number;
  discount: number;
  setDiscount: (value: number) => void;
  calculateTotal: (subtotal: number, isDelivery: boolean) => number;
  handleCancelOrder: (adjust_type: string) => void;
  handleUpdatOrder: () => void;
  resetFormFields: () => void;
  open: boolean;
  setOpen: (value: boolean) => void;
  customers: any[];
  filteredCustomers: any[];
  setFilteredCustomers: (customers: any[]) => void;
  sendorder: (customerId: string) => void;
  pagenumber: number;
  setPagenumber: (value: number) => void;
  total: { totalRecords: string; totalPage: string };
  setCustomerAdd: (value: boolean) => void;
  setIsEditing: (value: boolean) => void;
  isOrderModified?: boolean;
  selectedProducts: any[];
  isEditing: boolean;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  deliveryFee: number;
  setDeliveryFee: (value: number) => void;
  deliveryFeeEnabled?: boolean;
  isDelivery: boolean;
  parcelFee: number;
  setParcelFee: (value: number) => void;
}

const BillSummary: React.FC<BillSummaryProps> = ({
  subtotal,
  discount,
  setDiscount,
  calculateTotal,
  handleCancelOrder,
  handleUpdatOrder,
  resetFormFields,
  open,
  setOpen,
  customers,
  filteredCustomers,
  setFilteredCustomers,
  sendorder,
  pagenumber,
  setPagenumber,
  total,
  setCustomerAdd,
  setIsEditing,
  isOrderModified = true,
  selectedProducts,
  isEditing,
  searchTerm,
  setSearchTerm,
  deliveryFee,
  setDeliveryFee,
  deliveryFeeEnabled = false,
  isDelivery,
  parcelFee,
  setParcelFee,
}) => {
  const { handleBlur } = useDiscount(discount, subtotal, setIsEditing);
  const { userConfigration } = useUser();

  const [localDiscount, setLocalDiscount] = useState<string>(discount.toString());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [adjustType, setAdjustType] = useState<"refund" | "deduct_from_due">("refund");
  const [localDeliveryFee, setLocalDeliveryFee] = useState<string>(deliveryFee.toString());
  const [lastDeliveryFee, setLastDeliveryFee] = useState<number>(deliveryFee);
  const { checkPermission } = useCheckPermission();
  const buttonVisible = isEditing ? checkPermission(PERMISSIONS.ORDER.CANCEL) : true;
  const [localParcelFee, setLocalParcelFee] = useState<string>(parcelFee.toString());
  const parcelFeeEnabled = toBoolean(userConfigration?.parcelFeeEnabled);

  useEffect(() => {
    setLocalDeliveryFee(deliveryFee.toString());
  }, [deliveryFee]);

  useEffect(() => {
    setLocalParcelFee(parcelFee.toString());
  }, [parcelFee]);

  // Remove delivery fee automatically if switched to Table
useEffect(() => {
  if (!isDelivery) {
    setLastDeliveryFee(deliveryFee);
    setDeliveryFee(0);
    setLocalDeliveryFee("0");
  } else {
    setLocalDeliveryFee(deliveryFee.toString());
  }
}, [isDelivery, deliveryFee]);

  useEffect(() => {
    // Sync discount changes from parent (like reset)
    setLocalDiscount(discount.toString());
  }, [discount]);

  const handleParcelFeeChange = (value: string) => {
    setLocalParcelFee(value);

    const num = Number(value);

    if (isNaN(num) || num < 0) {
      setParcelFee(0);
      setLocalParcelFee("0");
    } else {
      setParcelFee(num);
    }
  };

  const handleDiscountChange = (value: string) => {
    setLocalDiscount(value);

    const num = Number(value);
    if (isNaN(num)) {
      setDiscount(0);
      return;
    }

    if (num >= subtotal) {
      setDiscount(subtotal - 1);
      setLocalDiscount((subtotal - 1).toString());
    } else if (num < 0) {
      setDiscount(0);
      setLocalDiscount("0");
    } else {
      setDiscount(num);
    }
  };

  const handleDeliveryFeeChange = (value: string) => {
    setLocalDeliveryFee(value);

    const num = Number(value);

    if (isNaN(num) || num < 0) {
      setDeliveryFee(0);
      setLocalDeliveryFee("0");
    } else {
      setDeliveryFee(num);
    }
  };

  // const grandTotal = calculateTotal(subtotal, isDelivery);
  const grandTotal = subtotal - discount + (isDelivery ? deliveryFee : 0) + parcelFee;

  return (
    <div className="bg-white p-3 border-t border-gray-200">
      <div className="space-y-2 text-sm">
        <div className="flex text-lg justify-between">
          <span>Sub total:</span>
          <span className="font-medium">{subtotal.toFixed(2)}</span>
        </div>

        {deliveryFeeEnabled && (
          <div className="flex justify-between text-lg items-center">
            <span>Delivery Fee:</span>
            <input
              type="number"
              placeholder="0"
              className="w-20 h-8 border border-black px-1 py-1 rounded-xl inline-block text-right"
              value={localDeliveryFee === "0" ? "" : localDeliveryFee}
              onChange={(e) => {
                const value = e.target.value;

                if (value === "") {
                  setLocalDeliveryFee("0");
                  setDeliveryFee(0);
                  return;
                }

                handleDeliveryFeeChange(value);
              }}
              onBlur={(e) => {
                if (e.target.value === "") {
                  setLocalDeliveryFee("0");
                  setDeliveryFee(0);
                }
              }}
              min={0}
              disabled={!deliveryFeeEnabled || !isDelivery}
            />
          </div>
        )}
        {parcelFeeEnabled && (
        <div className="flex justify-between text-lg items-center">
          <span>Parcel Fee:</span>
          <input
            type="number"
            placeholder="0"
            className="w-20 h-8 border border-black px-1 py-1 rounded-xl inline-block text-right"
            value={localParcelFee === "0" ? "" : localParcelFee}
            onChange={(e) => {
              const value = e.target.value;

              if (value === "") {
                setLocalParcelFee("0");
                setParcelFee(0);
                return;
              }

              handleParcelFeeChange(value);
            }}
            onBlur={(e) => {
              if (e.target.value === "") {
                setLocalParcelFee("0");
                setParcelFee(0);
              }
            }}
            min={0}
          />
        </div>
        )}

        <div className="flex justify-between text-lg items-center">
          <span>Discount:</span>
          <input
            type="number"
            placeholder="0"
            className="w-20 h-8 border border-black px-1 py-1 rounded-xl inline-block text-right"
            value={localDiscount === "0" ? "" : localDiscount}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "") {
                setLocalDiscount("0");
                setDiscount(0);
                return;
              }
              handleDiscountChange(value);
            }}
            onBlur={(e) => {
              if (e.target.value === "") {
                setLocalDiscount("0");
                setDiscount(0);
              }
              handleBlur();
            }}
            min={0}
            max={subtotal - 1}
          />
        </div>

        <div className="border-t border-gray-200 my-1"></div>
        <div className="flex justify-between text-xl font-bold">
          <span>Grand Total:</span>
          <span className="text-blue-600">{grandTotal.toFixed(2)}</span>
        </div>
      </div>

      <div className="flex justify-between gap-2 mt-4">
        {/* <CommonButtons className="px-8 py-3 w-48" label={isEditing ? "CANCEL ORDER" : "NEW ORDER"} variant="danger" onClick={isEditing ? handleCancelOrder : resetFormFields}/> */}
        {buttonVisible && <CommonButtons className="" label={isEditing ? "CANCEL ORDER" : "NEW ORDER"} variant="danger" onClick={() => setConfirmOpen(true)} />}

        <CommonButtons className="" label={isEditing ? "UPDATE ORDER" : "SEND ORDER"} onClick={handleUpdatOrder} disabled={!isOrderModified || selectedProducts.length === 0} />

        <SelectCustomer
          open={open}
          handleClose={() => setOpen(false)}
          customers={customers}
          filteredCustomers={filteredCustomers}
          setFilteredCustomers={setFilteredCustomers}
          sendorder={sendorder}
          pagenumber={pagenumber}
          setPagenumber={setPagenumber}
          total={total}
          setCustomerAdd={setCustomerAdd}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
        />
      </div>
      <ConfirmActionModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          isEditing ? handleCancelOrder(adjustType) : resetFormFields();
        }}
        isEditing={isEditing}
        adjustType={adjustType}
        setAdjustType={setAdjustType}
      />
    </div>
  );
};

export default BillSummary;
